import type { Dish } from '@/types/dish';
import { baseDish, t } from './mockCommon';

/**
 * Dessert 1 : Clafoutis aux clémentines et pistaches torréfiées (ID -11)
 */
function createClementineClafoutisDish(): Dish {
  return {
    ...baseDish,
    id: -11,
    name: t('onboarding.mockData.dishes.clementineClafoutis.name'),
    difficulty: 2,
    prep_time_min: 15,
    cook_time_min: 25,
    servings: 4,
    selected_image: 'none',
    allergens: `${t('onboarding.mockData.dishes.clementineClafoutis.allergens.lactose')}, ${t('onboarding.mockData.dishes.clementineClafoutis.allergens.eggs')}, ${t('onboarding.mockData.dishes.clementineClafoutis.allergens.nuts')}, ${t('onboarding.mockData.dishes.clementineClafoutis.allergens.gluten')}`,
    calories_kcal: 265,
    protein_g: 6.0,
    fat_g: 13.0,
    saturated_fat_g: 6.0,
    carbohydrates_g: 31.0,
    sugars_g: 22.0,
    dietary_fiber_g: 2.0,
    salt_sodium_g: 0.18,
    ingredients: [
      {
        id: 1,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.clementine'),
        quantity: 240, // 60 * 4
        unit: 'g',
        price: 2.0, // 0.5 * 4
      },
      {
        id: 2,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.roastedPistachio'),
        quantity: 40,
        unit: 'g',
        price: 1.8,
      },
      {
        id: 3,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.wholeMilk'),
        quantity: 200,
        unit: 'ml',
        price: 0.52,
      },
      {
        id: 4,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.heavyCream'),
        quantity: 80,
        unit: 'ml',
        price: 0.72,
      },
      {
        id: 5,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.sugar'),
        quantity: 60,
        unit: 'g',
        price: 0.28,
      },
      {
        id: 6,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.flour'),
        quantity: 32,
        unit: 'g',
        price: 0.1,
      },
      {
        id: 7,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.eggs'),
        quantity: 200,
        unit: 'g',
        price: 1.4,
      },
      {
        id: 8,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.vanillaSugar'),
        quantity: 6.4,
        unit: 'g',
        price: 0.24,
      },
      {
        id: 9,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.orangeZest'),
        quantity: 3.2,
        unit: 'g',
        price: 0.32,
      },
      {
        id: 10,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.salt'),
        quantity: 0.8,
        unit: 'g',
        price: 0.02,
      },
      {
        id: 11,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.clementineClafoutis.ingredients.mint'),
        quantity: 2,
        unit: 'g',
        price: 0.2,
      },
    ],
  };
}

/**
 * Dessert 2 : Mousse Légère au Chocolat Blanc et Griottes (ID -12)
 */
function createWhiteChocolateMousseDish(): Dish {
  return {
    ...baseDish,
    id: -12,
    name: t('onboarding.mockData.dishes.whiteChocMousse.name'),
    difficulty: 3,
    prep_time_min: 25,
    cook_time_min: 15,
    servings: 4,
    selected_image: 'none',
    allergens: `${t('onboarding.mockData.dishes.whiteChocMousse.allergens.lactose')}, ${t('onboarding.mockData.dishes.whiteChocMousse.allergens.eggs')}`,
    calories_kcal: 340,
    protein_g: 5.0,
    fat_g: 22.0,
    saturated_fat_g: 13.0,
    carbohydrates_g: 30.0,
    sugars_g: 28.0,
    dietary_fiber_g: 1.0,
    salt_sodium_g: 0.12,
    ingredients: [
      {
        id: 12,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.heavyCream'),
        quantity: 240,
        unit: 'ml',
        price: 2.16,
      },
      {
        id: 13,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.wholeMilk'),
        quantity: 80,
        unit: 'ml',
        price: 0.48,
      },
      {
        id: 14,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.whiteChocolate'),
        quantity: 140,
        unit: 'g',
        price: 3.5,
      },
      {
        id: 15,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.icingSugar'),
        quantity: 28,
        unit: 'g',
        price: 0.26,
      },
      {
        id: 16,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.eggYolk'),
        quantity: 24,
        unit: 'g',
        price: 0.64,
      },
      {
        id: 17,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.eggWhite'),
        quantity: 60,
        unit: 'g',
        price: 0.88,
      },
      {
        id: 18,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.cherries'),
        quantity: 120,
        unit: 'g',
        price: 2.6,
      },
      {
        id: 19,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.casterSugar'),
        quantity: 32,
        unit: 'g',
        price: 0.18,
      },
      {
        id: 20,
        category: 'alcohol',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.kirsch'),
        quantity: 12,
        unit: 'ml',
        price: 0.72,
      },
      {
        id: 21,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.lemonZest'),
        quantity: 2,
        unit: 'g',
        price: 0.1,
      },
      {
        id: 22,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.whiteChocMousse.ingredients.vanillaPod'),
        quantity: 1.6,
        unit: 'g',
        price: 1.28,
      },
    ],
  };
}

/**
 * Dessert 3 : Crème Brûlée à la Cardamome et Poire (ID -13)
 */
function createCardamomCremeBruleeDish(): Dish {
  return {
    ...baseDish,
    id: -13,
    name: t('onboarding.mockData.dishes.cardamomCremeBrulee.name'),
    difficulty: 3,
    prep_time_min: 25,
    cook_time_min: 35,
    servings: 4,
    selected_image: 'none',
    allergens: `${t('onboarding.mockData.dishes.cardamomCremeBrulee.allergens.lactose')}, ${t('onboarding.mockData.dishes.cardamomCremeBrulee.allergens.eggs')}`,
    calories_kcal: 370,
    protein_g: 5.0,
    fat_g: 27.0,
    saturated_fat_g: 15.0,
    carbohydrates_g: 28.0,
    sugars_g: 25.0,
    dietary_fiber_g: 1.0,
    salt_sodium_g: 0.1,
    ingredients: [
      {
        id: 24,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.cardamomCremeBrulee.ingredients.heavyCream'),
        quantity: 400,
        unit: 'ml',
        price: 3.2,
      },
      {
        id: 25,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.cardamomCremeBrulee.ingredients.wholeMilk'),
        quantity: 160,
        unit: 'ml',
        price: 0.66,
      },
      {
        id: 26,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.cardamomCremeBrulee.ingredients.eggYolk'),
        quantity: 80,
        unit: 'g',
        price: 1.6,
      },
      {
        id: 27,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.cardamomCremeBrulee.ingredients.whiteSugar'),
        quantity: 88,
        unit: 'g',
        price: 0.4,
      },
      {
        id: 28,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.cardamomCremeBrulee.ingredients.pearWilliams'),
        quantity: 200,
        unit: 'g',
        price: 1.56,
      },
      {
        id: 29,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cardamomCremeBrulee.ingredients.lemonZest'),
        quantity: 2,
        unit: 'g',
        price: 0.12,
      },
      {
        id: 30,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cardamomCremeBrulee.ingredients.cardamom'),
        quantity: 1.6,
        unit: 'g',
        price: 0.28,
      },
      {
        id: 31,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cardamomCremeBrulee.ingredients.vanillaExtract'),
        quantity: 4,
        unit: 'ml',
        price: 1.0,
      },
      {
        id: 32,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.cardamomCremeBrulee.ingredients.brownSugar'),
        quantity: 32,
        unit: 'g',
        price: 0.24,
      },
    ],
  };
}

/**
 * Dessert 4 : Île Flottante sur Crème Anglaise au Safran (ID -14)
 */
function createFloatingIslandDish(): Dish {
  return {
    ...baseDish,
    id: -14,
    name: t('onboarding.mockData.dishes.floatingIsland.name'),
    difficulty: 3,
    prep_time_min: 25,
    cook_time_min: 20,
    servings: 4,
    selected_image: 'none',
    allergens: `${t('onboarding.mockData.dishes.floatingIsland.allergens.lactose')}, ${t('onboarding.mockData.dishes.floatingIsland.allergens.eggs')}`,
    calories_kcal: 270,
    protein_g: 7.0,
    fat_g: 11.0,
    saturated_fat_g: 6.0,
    carbohydrates_g: 34.0,
    sugars_g: 30.0,
    dietary_fiber_g: 0.2,
    salt_sodium_g: 0.2,
    ingredients: [
      {
        id: 34,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.floatingIsland.ingredients.wholeMilk'),
        quantity: 600,
        unit: 'ml',
        price: 0.9,
      },
      {
        id: 35,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.floatingIsland.ingredients.heavyCream'),
        quantity: 80,
        unit: 'ml',
        price: 0.72,
      },
      {
        id: 36,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.floatingIsland.ingredients.sugar'),
        quantity: 96,
        unit: 'g',
        price: 0.48,
      },
      {
        id: 37,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.floatingIsland.ingredients.eggs'),
        quantity: 240,
        unit: 'g',
        price: 2.2,
      },
      {
        id: 38,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.floatingIsland.ingredients.lemonZest'),
        quantity: 1.6,
        unit: 'g',
        price: 0.2,
      },
      {
        id: 39,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.floatingIsland.ingredients.vanillaExtract'),
        quantity: 4,
        unit: 'ml',
        price: 0.48,
      },
      {
        id: 40,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.floatingIsland.ingredients.saffron'),
        quantity: 0.2,
        unit: 'g',
        price: 3.0,
      },
      {
        id: 42,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.floatingIsland.ingredients.icingSugar'),
        quantity: 12,
        unit: 'g',
        price: 0.1,
      },
      {
        id: 43,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.floatingIsland.ingredients.freshHerbs'),
        quantity: 2,
        unit: 'g',
        price: 0.3,
      },
    ],
  };
}

/**
 * Dessert 5 : Financier à la Noisette et Ganache de Gianduja (ID -15)
 */
function createHazelnutFinancierDish(): Dish {
  return {
    ...baseDish,
    id: -15,
    name: t('onboarding.mockData.dishes.hazelnutFinancier.name'),
    difficulty: 3,
    prep_time_min: 25,
    cook_time_min: 15,
    servings: 4,
    selected_image: 'none',
    allergens: `${t('onboarding.mockData.dishes.hazelnutFinancier.allergens.lactose')}, ${t('onboarding.mockData.dishes.hazelnutFinancier.allergens.eggs')}, ${t('onboarding.mockData.dishes.hazelnutFinancier.allergens.nuts')}, ${t('onboarding.mockData.dishes.hazelnutFinancier.allergens.gluten')}`,
    calories_kcal: 430,
    protein_g: 6.0,
    fat_g: 28.0,
    saturated_fat_g: 11.0,
    carbohydrates_g: 37.0,
    sugars_g: 27.0,
    dietary_fiber_g: 3.0,
    salt_sodium_g: 0.18,
    ingredients: [
      {
        id: 44,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.hazelnutPowder'),
        quantity: 72,
        unit: 'g',
        price: 1.8,
      },
      {
        id: 45,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.wheatFlour'),
        quantity: 24,
        unit: 'g',
        price: 0.16,
      },
      {
        id: 46,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.butter'),
        quantity: 72,
        unit: 'g',
        price: 0.88,
      },
      {
        id: 47,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.icingSugar'),
        quantity: 72,
        unit: 'g',
        price: 0.52,
      },
      {
        id: 48,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.eggWhite'),
        quantity: 120,
        unit: 'g',
        price: 0.8,
      },
      {
        id: 49,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.bakingPowder'),
        quantity: 3.2,
        unit: 'g',
        price: 0.04,
      },
      {
        id: 50,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.orangeZest'),
        quantity: 1.6,
        unit: 'g',
        price: 0.08,
      },
      {
        id: 51,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.vanillaExtract'),
        quantity: 2,
        unit: 'ml',
        price: 0.32,
      },
      {
        id: 53,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.heavyCream'),
        quantity: 80,
        unit: 'ml',
        price: 0.56,
      },
      {
        id: 54,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.giandujaChocolate'),
        quantity: 100,
        unit: 'g',
        price: 2.4,
      },
      {
        id: 55,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.hazelnutPralinePaste'),
        quantity: 24,
        unit: 'g',
        price: 0.96,
      },
      {
        id: 56,
        category: 'grocery',
        name: t('onboarding.mockData.dishes.hazelnutFinancier.ingredients.roastedHazelnuts'),
        quantity: 16,
        unit: 'g',
        price: 0.4,
      },
    ],
  };
}

// Map des factory functions par ID pour les desserts
export const dessertFactories: Record<number, () => Dish> = {
  [-11]: createClementineClafoutisDish,
  [-12]: createWhiteChocolateMousseDish,
  [-13]: createCardamomCremeBruleeDish,
  [-14]: createFloatingIslandDish,
  [-15]: createHazelnutFinancierDish,
};
